import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Scale, Search, ShieldAlert, Gavel, Timer, Users, FileText,
  Lock, ArrowRight, Menu, X, CheckCircle2,
} from "lucide-react";
import JeaButton from "@/components/jea/JeaButton";
import { cn } from "@/lib/utils";

const NAV_ITEMS = [
  { href: "#features",     label: "Features" },
  { href: "#how-it-works", label: "How It Works" },
  { href: "#pricing",      label: "Pricing" },
];

const FEATURES = [
  { Icon: Search,      title: "Multi-Broker Asset Discovery", body: "Query 7 leading data brokers in parallel. Consolidate results into a single actionable asset profile." },
  { Icon: ShieldAlert, title: "Bankruptcy-Safe Enforcement",  body: "Automatic PACER monitoring blocks illegal enforcement during § 362 stays. Never accidentally violate a bankruptcy." },
  { Icon: Gavel,       title: "Guided Enforcement Workflows", body: "Wage garnishment, bank levy, and property seizure — every pathway includes state-specific rules and exemption checks." },
  { Icon: Timer,       title: "Deadline & Interest Tracking", body: "Portfolio-wide timeline of judgment expirations, renewals, and lien-recording windows. Accrued interest auto-calculated per state statute." },
  { Icon: Users,       title: "PRO Team Management",          body: "Role-based access for firms and agencies. Owner, admin, attorney, collector, and read-only roles with per-judgment notification routing." },
  { Icon: FileText,    title: "Attorney-Ready Reports",       body: "One-click PDF reports and handoff-ready attorney memos. Change logs, enforcement recommendations, and recovery probability estimates." },
];

const STEPS = [
  { n: "01", title: "Add your judgment",   body: "Upload judgment details in a guided wizard. Set tier (Bronze, Silver, Gold) to control automated query frequency." },
  { n: "02", title: "Run a broker query",body: "Pick brokers or a bundle. Our engine runs them in parallel and delivers a consolidated asset profile in seconds." },
  { n: "03", title: "Enforce with confidence", body: "Follow guided workflows for wage garnishment, bank levy, or property seizure. Every step is state-aware and § 362-safe." },
];

const PLAN_FEATURES = [
  "All 7 data brokers included",
  "50-state statute coverage",
  "Automated bankruptcy monitoring",
  "Enforcement workflow guidance",
  "PDF reports + attorney memos",
];

const PLANS = [
  { title: "3 MONTHS",  price: "$199 – $398", body: "1 to 3 judgments. Automated broker queries at your chosen tier.", highlight: false, ctaVariant: "outline-pink", ctaLabel: "Get Started" },
  { title: "6 MONTHS",  price: "$299 – $598", body: "Save vs monthly · Best for most creditors.",                    highlight: true,  ctaVariant: "primary-pink", ctaLabel: "Get Started", pill: "BEST FOR MOST" },
  { title: "12 MONTHS", price: "$499 – $998", body: "Best long-term value. Automated tier queries for a full year.", highlight: false, ctaVariant: "outline-pink", ctaLabel: "Get Started" },
  { title: "PRO",       price: "$999/mo",   priceSub: <><s className="text-text-muted">$11,988</s> · <span className="text-success">2 MONTHS FREE</span> annually</>, body: "Unlimited judgments, team management, PRO broker rates.", highlight: false, ctaVariant: "outline-pink", ctaLabel: "Start PRO Trial", gold: true },
];

const TRUST_BADGES = [
  "AES-256 at rest",
  "TLS 1.3 in transit",
  "Field-level SSN encryption",
  "Immutable audit log",
  "SOC 2 Type II target",
];

const LandingPage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-bg-primary text-white" data-testid="landing-page">
      {/* ── Sticky nav ───────────────────────────────────────────── */}
      <header
        className={cn(
          "fixed top-0 inset-x-0 z-50 transition-colors",
          scrolled ? "bg-[#091424]/90 backdrop-blur-md border-b border-border-default" : "bg-transparent"
        )}
        data-testid="landing-nav"
      >
        <div className="max-w-[1320px] mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <Scale className="text-accent-pink" size={20} />
            <span className="text-white font-extrabold text-[16px]">
              Judgment Enforcement <span className="text-accent-pink">AI</span>
            </span>
          </Link>
          <nav className="hidden lg:flex items-center gap-8">
            {NAV_ITEMS.map((n) => (
              <a
                key={n.href}
                href={n.href}
                className="text-text-secondary hover:text-white text-[14px] font-medium transition-colors"
                data-testid={`landing-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n.label}
              </a>
            ))}
            <Link to="/login" className="text-text-secondary hover:text-white text-[14px] font-medium transition-colors" data-testid="landing-login">
              Log In
            </Link>
            <Link to="/demo">
              <JeaButton size="sm" variant="outline-pink" data-testid="landing-demo-nav">Try Demo</JeaButton>
            </Link>
            <Link to="/register">
              <JeaButton size="sm" data-testid="landing-signup-nav">Sign Up Free</JeaButton>
            </Link>
          </nav>
          <button
            type="button"
            className="lg:hidden p-2 text-text-secondary hover:text-white"
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="landing-mobile-toggle"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
        {mobileOpen && (
          <div className="lg:hidden bg-bg-primary border-t border-border-default" data-testid="landing-mobile-menu">
            <div className="max-w-[1320px] mx-auto px-6 py-4 flex flex-col gap-3">
              {NAV_ITEMS.map((n) => (
                <a key={n.href} href={n.href} onClick={() => setMobileOpen(false)} className="text-text-secondary hover:text-white py-2 text-[14px] font-medium">
                  {n.label}
                </a>
              ))}
              <Link to="/login" onClick={() => setMobileOpen(false)} className="text-text-secondary hover:text-white py-2 text-[14px] font-medium">Log In</Link>
              <div className="flex gap-2 pt-2">
                <Link to="/demo" className="flex-1"><JeaButton size="sm" variant="outline-pink" fullWidth>Try Demo</JeaButton></Link>
                <Link to="/register" className="flex-1"><JeaButton size="sm" fullWidth>Sign Up Free</JeaButton></Link>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* ── Hero ───────────────────────────────────────────────── */}
      <section
        className="relative pt-32 pb-24 min-h-[80vh] flex items-center overflow-hidden"
        data-testid="landing-hero"
      >
        {/* Subtle radial accent glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 900px 460px at 50% 30%, rgba(251,111,146,0.10), transparent 60%)",
          }}
        />
        <div className="relative max-w-[1320px] mx-auto px-6 w-full text-center">
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="text-accent-pink text-[12px] tracking-[0.1em] font-semibold uppercase mb-4">
              Judgment Enforcement AI
            </div>
            <h1 className="font-extrabold tracking-[-0.02em] text-[44px] sm:text-[56px] lg:text-[64px] leading-[1.05] max-w-[880px] mx-auto">
              Recover More.<br />Chase Less.
            </h1>
            <p className="text-text-secondary text-[18px] lg:text-[20px] max-w-[640px] mx-auto mt-6 leading-relaxed">
              The AI-powered platform judgment creditors use to locate debtor assets,
              monitor for bankruptcy, and execute enforcement — with courtroom-grade legal precision.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/demo"><JeaButton size="lg" className="gap-2" data-testid="landing-hero-demo">Start Free Demo <ArrowRight size={16} /></JeaButton></Link>
              <a href="#pricing"><JeaButton size="lg" variant="outline-pink" data-testid="landing-hero-pricing">See Pricing</JeaButton></a>
            </div>
            <div className="mt-10 flex flex-wrap justify-center gap-2">
              {["7 Data Brokers Integrated","50-State Statute Coverage","SOC 2 Ready","§ 362 Auto-Compliance"].map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1.5 bg-bg-secondary/60 border border-border-default text-text-secondary text-[12px] font-medium px-3 py-1.5 rounded-full"
                >
                  <CheckCircle2 size={12} className="text-accent-pink" /> {t}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Metrics strip ─────────────────────────────────────── */}
      <section className="bg-bg-secondary border-y border-border-default" data-testid="landing-metrics">
        <div className="max-w-[1320px] mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { n: "$998",  d: "3 judgments, 12 months, all-inclusive" },
            { n: "50",    d: "US states + DC — full statute coverage" },
            { n: "7",     d: "Data brokers unified in one query" },
            { n: "< 5s",  d: "Parallel broker orchestration" },
          ].map((m) => (
            <div key={m.n} className="text-center">
              <div className="jea-mono text-accent-pink font-extrabold text-[32px] lg:text-[36px]">{m.n}</div>
              <div className="text-text-secondary text-[13px] mt-1">{m.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Features ─────────────────────────────────────────── */}
      <section id="features" className="py-24" data-testid="landing-features">
        <div className="max-w-[1320px] mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-white font-bold text-[32px] lg:text-[36px] tracking-tight">
              Everything you need to enforce a judgment
            </h2>
            <p className="text-text-secondary text-[16px] mt-3 max-w-[640px] mx-auto">
              From discovery to satisfaction — one platform, complete workflow.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map(({ Icon, title, body }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.3, delay: i * 0.05 }}
                className="bg-bg-secondary border border-border-default rounded-[16px] p-8 hover:border-accent-pink/40 transition-colors"
                data-testid={`landing-feature-${i}`}
              >
                <Icon className="text-accent-pink" size={30} />
                <h3 className="text-white font-bold text-[18px] mt-4">{title}</h3>
                <p className="text-text-secondary text-[14px] mt-2 leading-relaxed">{body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ─────────────────────────────────────── */}
      <section id="how-it-works" className="py-24 bg-bg-secondary/40" data-testid="landing-how">
        <div className="max-w-[1320px] mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-white font-bold text-[32px] lg:text-[36px] tracking-tight">
              From judgment to recovery in three steps
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {STEPS.map((s) => (
              <div key={s.n} className="text-left">
                <div className="jea-mono text-accent-pink font-extrabold text-[40px] leading-none">{s.n}</div>
                <h3 className="text-white font-bold text-[20px] mt-4">{s.title}</h3>
                <p className="text-text-secondary text-[15px] mt-2 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ─────────────────────────────────────── */}
      <section id="pricing" className="py-24" data-testid="landing-pricing">
        <div className="max-w-[1320px] mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-white font-bold text-[32px] lg:text-[36px] tracking-tight">
              Transparent, predictable pricing
            </h2>
            <p className="text-text-secondary text-[16px] mt-3 max-w-[560px] mx-auto">
              No contingency fee. No hidden costs. Only pay for the queries you run.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {PLANS.map((p) => (
              <div
                key={p.title}
                className={cn(
                  "bg-bg-secondary rounded-[16px] p-6 relative flex flex-col",
                  p.highlight ? "border-2 border-accent-pink shadow-[0_0_32px_rgba(251,111,146,0.15)]" : "border border-border-default",
                  p.gold && "border-l-4 border-l-warning"
                )}
                data-testid={`landing-plan-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {p.pill && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent-pink text-[#091424] text-[10px] font-bold px-3 py-1 rounded-full tracking-[0.08em]">
                    {p.pill}
                  </span>
                )}
                <div className="jea-caption">{p.title}</div>
                <div className="jea-mono text-accent-pink font-extrabold text-[28px] mt-2">{p.price}</div>
                {p.priceSub && <div className="text-text-secondary text-[12px] mt-1">{p.priceSub}</div>}
                <p className="text-text-secondary text-[13px] mt-3">{p.body}</p>
                <ul className="mt-4 space-y-1.5 flex-1">
                  {PLAN_FEATURES.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-[12px] text-text-secondary">
                      <CheckCircle2 size={12} className="text-accent-pink mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Link to="/register" className="mt-5">
                  <JeaButton fullWidth variant={p.ctaVariant} size="sm">{p.ctaLabel}</JeaButton>
                </Link>
              </div>
            ))}
          </div>
          <p className="text-text-muted text-[13px] text-center mt-8">
            All plans include the same features and 7 data brokers. Per-query broker costs apply separately at transparent rates. No free trial. No contingency fee.
          </p>
        </div>
      </section>

      {/* ── Trust ─────────────────────────────────────── */}
      <section className="py-24 bg-bg-secondary/40" data-testid="landing-trust">
        <div className="max-w-[1320px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-12 items-center">
          <div>
            <h2 className="text-white font-bold text-[32px] tracking-tight">
              Built for enterprise-grade compliance
            </h2>
            <ul className="mt-6 space-y-3">
              {TRUST_BADGES.map((b) => (
                <li key={b} className="flex items-start gap-3 text-[15px] text-text-secondary">
                  <Lock size={16} className="text-accent-pink mt-1 shrink-0" /> {b}
                </li>
              ))}
            </ul>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {["SOC 2", "GDPR", "CCPA", "AES-256", "TLS 1.3", "Audit Log"].map((tag) => (
              <div
                key={tag}
                className="bg-bg-secondary border border-border-default rounded-[12px] p-4 text-center hover:border-accent-pink/40 transition-colors"
              >
                <Lock className="text-accent-pink mx-auto" size={18} />
                <div className="text-white text-[12px] font-bold mt-2 tracking-[0.06em]">{tag}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ─────────────────────────────────────── */}
      <section className="bg-bg-secondary py-24" data-testid="landing-final-cta">
        <div className="max-w-[1320px] mx-auto px-6 text-center">
          <h2 className="font-extrabold text-white text-[32px] lg:text-[36px] tracking-tight">
            Try Judgment Enforcement AI free — no credit card required
          </h2>
          <p className="text-text-secondary text-[18px] mt-4 max-w-[640px] mx-auto">
            Explore the platform with a full 15-debtor demo portfolio. Sign up when you're ready to run real queries.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/demo"><JeaButton size="lg" data-testid="landing-final-demo">Launch Demo</JeaButton></Link>
            <Link to="/register"><JeaButton size="lg" variant="outline-pink" data-testid="landing-final-signup">Create Free Account</JeaButton></Link>
          </div>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────── */}
      <footer className="bg-bg-primary border-t border-border-default py-12" data-testid="landing-footer">
        <div className="max-w-[1320px] mx-auto px-6 grid grid-cols-1 md:grid-cols-[2fr_3fr] gap-10">
          <div>
            <Link to="/" className="flex items-center gap-2">
              <Scale className="text-accent-pink" size={18} />
              <span className="text-white font-extrabold text-[15px]">Judgment Enforcement <span className="text-accent-pink">AI</span></span>
            </Link>
            <p className="text-text-secondary text-[13px] mt-3 max-w-[360px]">
              AI-powered post-judgment asset discovery and enforcement for creditors, agencies, and law firms.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-[13px]">
            {[
              { title: "Product",  links: [["Features", "#features"], ["Pricing", "#pricing"], ["Demo", "/demo"], ["Log In", "/login"]] },
              { title: "Company",  links: [["About", "#"], ["Careers", "#"], ["Contact", "#"]] },
              { title: "Legal",    links: [["Terms", "#"], ["Privacy", "#"], ["Compliance", "#"]] },
              { title: "Resources",links: [["Docs", "#"], ["Support", "#"], ["Status", "#"]] },
            ].map((col) => (
              <div key={col.title}>
                <div className="jea-caption text-text-secondary">{col.title}</div>
                <ul className="mt-3 space-y-2">
                  {col.links.map(([label, href]) => (
                    <li key={label}>
                      {href.startsWith("/") || href.startsWith("#") ? (
                        <a href={href} className="text-text-secondary hover:text-accent-pink">{label}</a>
                      ) : (
                        <a href={href} className="text-text-secondary hover:text-accent-pink">{label}</a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <div className="max-w-[1320px] mx-auto px-6 mt-10 pt-6 border-t border-border-default text-text-muted text-[12px] flex flex-wrap items-center justify-between gap-2">
          <span>© 2026 Judgment Enforcement AI. Not legal advice. Consult a licensed attorney.</span>
          <span className="jea-mono">v1.0</span>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
